import { FC, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import axiosInstance from 'apis/axiosInstance';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import useMatchMutate from 'hooks/useMatchMutate';
import useInsuranceStatus from 'hooks/useInsuranceStatus';
import InsuranceSwitch from '../InsuranceSwitch/InsuranceSwitch';
import { INSURANCE_PROPERTY } from '../InsuranceToggle/InsuranceToggle';

type ModalType = 'accepted' | 'in-network' | 'out-of-network';

const AllProviderRow: FC = () => {
  const { data, isLoading: isDataLoading, mutate } = useInsuranceStatus();

  const [isAcceptedModalDisplayed, setIsAcceptedModalDisplayed] =
    useState(false);

  const [isInNetworkModalDisplayed, setIsInNetworkModalDisplayed] =
    useState(false);

  const [isOutOfNetworkModalDisplayed, setIsOutOfNetworkModalDisplayed] =
    useState(false);

  const matchMutate = useMatchMutate();

  const isChecked = data?.isSameAcceptedStatus ? data.isAllActive : true;
  const isInNetwork = data?.isAllInNetwork || false;
  const isOutOfNetwork = data?.isAllOutOfNetwork || false;

  const onChange = (type: ModalType) => {
    if (type === 'accepted') {
      setIsAcceptedModalDisplayed(true);
    }

    if (type === 'in-network') {
      setIsInNetworkModalDisplayed(true);
    }

    if (type === 'out-of-network') {
      setIsOutOfNetworkModalDisplayed(true);
    }
  };

  const onConfirm = async (type: ModalType) => {
    let payload = null;

    if (type === 'accepted') {
      payload = {
        [INSURANCE_PROPERTY.IS_ACTIVE]: data?.isSameAcceptedStatus
          ? !data.isAllActive
          : false,
      };
    }

    if (type === 'in-network') {
      payload = {
        [INSURANCE_PROPERTY.IS_IN_NETWORK]:
          data?.isAllInNetwork === undefined ? undefined : !data.isAllInNetwork,
      };
    }

    if (type === 'out-of-network') {
      payload = {
        [INSURANCE_PROPERTY.IS_OUT_OF_NETWORK]:
          data?.isAllOutOfNetwork === undefined
            ? undefined
            : !data.isAllOutOfNetwork,
      };
    }

    try {
      await axiosInstance.put('/insurances', payload);
      await matchMutate(/\/admin\/insurances\?[\s\S]+/, { revalidate: true });
      await mutate();
    } catch (error) {}
  };

  const renderConfirmationModal = () => {
    if (isAcceptedModalDisplayed) {
      const message = !isChecked
        ? `Turning this on will mark all insurance providers as accepted. This action will override the current settings for each provider`
        : `Turning this off will mark all insurance providers as not accepted. This action will override the current settings for each provider`;

      return (
        <ConfirmationModal
          title={`${
            !isChecked ? 'Enable all' : 'Disable all'
          } insurance providers?`}
          description={
            <div className="text-14 leading-[2.1rem] mt-1.6">
              <p>{message}</p>
              <p className="mt-1">Do you want to continue?</p>
            </div>
          }
          onClose={() => setIsAcceptedModalDisplayed(false)}
          onSubmit={async () => {
            await onConfirm('accepted');
          }}
          submitBtnTitle={!isChecked ? 'Enable All' : 'Disable All'}
        />
      );
    }

    if (isInNetworkModalDisplayed) {
      const message = !isInNetwork
        ? `Turning this on will mark all practitioners as accepted. This action will override the current settings for each provider`
        : `Turning this off will mark all practitioners as not accepted. This action will override the current settings for each provider`;

      return (
        <ConfirmationModal
          title={`${
            !isInNetwork ? 'Enable all' : 'Disable all'
          } practitioners?`}
          description={
            <div className="text-14 leading-[2.1rem] mt-1.6">
              <p>{message}</p>
              <p className="mt-1">Do you want to continue?</p>
            </div>
          }
          onClose={() => setIsInNetworkModalDisplayed(false)}
          onSubmit={async () => {
            await onConfirm('in-network');
          }}
          submitBtnTitle={!isInNetwork ? 'Enable All' : 'Disable All'}
        />
      );
    }

    if (isOutOfNetworkModalDisplayed) {
      const message = !isOutOfNetwork
        ? `Turning this on will mark all practitioners as accepted. This action will override the current settings for each provider`
        : `Turning this off will mark all practitioners as not accepted. This action will override the current settings for each provider`;

      return (
        <ConfirmationModal
          title={`${
            !isOutOfNetwork ? 'Enable all' : 'Disable all'
          } practitioners?`}
          description={
            <div className="text-14 leading-[2.1rem] mt-1.6">
              <p>{message}</p>
              <p className="mt-1">Do you want to continue?</p>
            </div>
          }
          onClose={() => setIsOutOfNetworkModalDisplayed(false)}
          onSubmit={async () => {
            await onConfirm('out-of-network');
          }}
          submitBtnTitle={!isOutOfNetwork ? 'Enable All' : 'Disable All'}
        />
      );
    }
  };

  return (
    <>
      {renderConfirmationModal()}

      <tr
        className="bg-white border-b border-light-grey px-4 group hover:bg-lightPink items-center cursor-pointer"
        role="row"
        style={{ display: 'flex', flex: '1 0 auto', minWidth: '0px' }}
      >
        <td
          className="text-left p-0 py-1.9 my-auto"
          style={{
            boxSizing: 'border-box',
            flex: '150 0 auto',
            minWidth: '0px',
            width: '150px',
          }}
          role="cell"
        >
          <div className="flex gap-x-0.8">
            <span>All providers</span>
            <button data-for="allProviderToggle" data-tip="">
              <InfoIcon className="w-1.6 h-1.6 all-child:fill-mid-grey" />
            </button>
            <ReactTooltip
              id="allProviderToggle"
              effect="solid"
              place="right"
              className="!bg-white !rounded-[0.8rem] !w-[21rem] !h-[auto] z-[9999] flex justify-center items-center !opacity-100 shadow-elevation-07 after:hidden before:hidden !px-[10px] !py-[4px]"
            >
              <span className="text-11 text-darkest-grey flex items-center font-normal">
                Controls all insurance providers. Turning this on or off will
                override every individual provider setting.
              </span>
            </ReactTooltip>
          </div>
        </td>
        <td
          className="text-left p-0 py-1.9 my-auto"
          style={{
            boxSizing: 'border-box',
            flex: '150 0 auto',
            minWidth: '0px',
            width: '150px',
          }}
          role="cell"
        >
          <InsuranceSwitch
            isChecked={isChecked}
            isLoading={isDataLoading}
            onChange={() => onChange('accepted')}
            isBlurSwitch={!data?.isSameAcceptedStatus}
          />
        </td>
        <td
          className="text-left p-0 py-1.9 my-auto"
          style={{
            boxSizing: 'border-box',
            flex: '150 0 auto',
            minWidth: '0px',
            width: '150px',
          }}
          role="cell"
        >
          <InsuranceSwitch
            isChecked={isInNetwork}
            isLoading={isDataLoading}
            onChange={() => onChange('in-network')}
          />
        </td>
        <td
          className="text-left p-0 py-1.9 my-auto"
          style={{
            boxSizing: 'border-box',
            flex: '150 0 auto',
            minWidth: '0px',
            width: '150px',
          }}
          role="cell"
        >
          <InsuranceSwitch
            isChecked={isOutOfNetwork}
            isLoading={isDataLoading}
            onChange={() => onChange('out-of-network')}
          />
        </td>
      </tr>
    </>
  );
};

export default AllProviderRow;
